import React, { useCallback, useRef } from 'react'

import { useDynamicIsland } from './useDynamicIsland'
import { FlashContent } from './FlashContent'
import type { FlashMessageType } from './FlashContent'

export function useFlashMessage() {
  const { pushRows, popRows } = useDynamicIsland()

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flashIdRef = useRef<string | null>(null)

  const dismiss = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (flashIdRef.current) {
      popRows(flashIdRef.current)
      flashIdRef.current = null
    }
  }, [popRows])

  const showFlash = useCallback(
    (message: string, type: FlashMessageType = 'info', duration = 4000) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      const id = `flash-${Date.now()}`
      flashIdRef.current = id

      pushRows(
        [
          [
            {
              id,
              meta: { flashType: type },
              component: React.createElement(FlashContent, {
                message,
                type,
                onDismiss: dismiss,
              }),
            },
          ],
        ],
        id
      )

      // The timer is intentionally NOT cleaned up on unmount. Since
      // popRows(id) is a no-op when the ID no longer matches the current
      // push (e.g. a settings navigator replaced the flash), the timer
      // is safe to fire at any time. This lets the flash stay visible
      // for its full duration even when the calling component navigates
      // away and unmounts.
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        flashIdRef.current = null
        popRows(id)
      }, duration)
    },
    [pushRows, popRows, dismiss]
  )

  return { showFlash, dismiss }
}
